/**
 * Customer Portal Dashboard API Client
 * HTTP client for dashboard data fetching
 */

import { getHttpClient } from '@spcc/core'
import type {
  DashboardData,
  FetchDashboardRequest,
  ChartData,
  LiveFeedData,
  RankingsData,
  FactoryDetailData,
  EntityDetailData,
  EntityType,
  OrdersValueKpi,
} from '../../domain/types'
import { DASHBOARD_ENDPOINTS } from '../../domain/constants'
import {
  validateDashboardResponse,
  validateChartResponse,
  validateLiveFeedResponse,
  validateRankingsResponse,
  validateFactoryDetailResponse,
  validateEntityDetailResponse,
  validateOrdersValueResponse,
} from '../../api/api-types'
import {
  transformDashboardDtoToDomain,
  transformChartDtoToDomain,
  transformLiveFeedDtoToDomain,
  transformRankingsDtoToDomain,
  transformFactoryDetailDtoToDomain,
  transformEntityDetailDtoToDomain,
} from '../../api/transformers'

// ============================================
// Query Builders
// ============================================

function buildDashboardParams(request?: FetchDashboardRequest, limit?: number): URLSearchParams {
  const params = new URLSearchParams()

  if (request?.from) {
    params.set('from', request.from)
  }
  if (request?.to) {
    params.set('to', request.to)
  }
  if (request?.factoryIds && request.factoryIds.length > 0) {
    params.set('factoryIds', request.factoryIds.join(','))
  }
  if (typeof limit === 'number' && Number.isFinite(limit)) {
    params.set('limit', String(limit))
  }

  return params
}

// ============================================
// API Client
// ============================================

export const customerPortalDashboardApi = {
  /**
   * Fetch aggregated dashboard data
   * Single endpoint that returns all KPIs
   */
  async getDashboard(request?: FetchDashboardRequest): Promise<DashboardData> {
    const http = getHttpClient()
    const params = buildDashboardParams(request)
    const queryString = params.toString()
    const url = queryString
      ? `${DASHBOARD_ENDPOINTS.DASHBOARD}?${queryString}`
      : DASHBOARD_ENDPOINTS.DASHBOARD

    const response = await http.get<unknown>(url)
    const dto = validateDashboardResponse(response.data)
    return transformDashboardDtoToDomain(dto)
  },

  /**
   * Fetch chart time-series data
   */
  async getChartData(request?: FetchDashboardRequest): Promise<ChartData> {
    const http = getHttpClient()
    const params = buildDashboardParams(request)
    const queryString = params.toString()
    const url = queryString
      ? `${DASHBOARD_ENDPOINTS.CHART}?${queryString}`
      : DASHBOARD_ENDPOINTS.CHART

    const response = await http.get<unknown>(url)
    const dto = validateChartResponse(response.data)
    return transformChartDtoToDomain(dto)
  },

  /**
   * Fetch live feed of shipments at factory
   * Optionally filtered to specific factories
   */
  async getLiveFeed(request?: Pick<FetchDashboardRequest, 'factoryIds'>): Promise<LiveFeedData> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (request?.factoryIds && request.factoryIds.length > 0) {
      params.set('factoryIds', request.factoryIds.join(','))
    }

    const queryString = params.toString()
    const url = queryString
      ? `${DASHBOARD_ENDPOINTS.LIVE_FEED}?${queryString}`
      : DASHBOARD_ENDPOINTS.LIVE_FEED

    const response = await http.get<unknown>(url)
    const dto = validateLiveFeedResponse(response.data)
    return transformLiveFeedDtoToDomain(dto)
  },

  /**
   * Fetch top rankings for products/customers/cities
   */
  async getRankings(
    request?: FetchDashboardRequest,
    limit = 7
  ): Promise<RankingsData> {
    const http = getHttpClient()
    const params = buildDashboardParams(request, limit)
    const queryString = params.toString()
    const url = queryString
      ? `${DASHBOARD_ENDPOINTS.RANKINGS}?${queryString}`
      : DASHBOARD_ENDPOINTS.RANKINGS

    const response = await http.get<unknown>(url)
    const dto = validateRankingsResponse(response.data)
    return transformRankingsDtoToDomain(dto)
  },

  async getFactoryDetail(
    factoryId: string,
    request?: FetchDashboardRequest,
    limit = 7
  ): Promise<FactoryDetailData> {
    const http = getHttpClient()
    const params = buildDashboardParams(request, limit)
    params.set('factoryId', factoryId)
    const queryString = params.toString()
    const url = `${DASHBOARD_ENDPOINTS.FACTORY_DETAIL}?${queryString}`

    const response = await http.get<unknown>(url)
    const dto = validateFactoryDetailResponse(response.data)
    return transformFactoryDetailDtoToDomain(dto)
  },

  async getEntityDetail(
    entityType: EntityType,
    entityId: string,
    request?: FetchDashboardRequest,
    limit = 7
  ): Promise<EntityDetailData> {
    const http = getHttpClient()
    const params = buildDashboardParams(request, limit)
    const endpointMap: Record<EntityType, string> = {
      customer: DASHBOARD_ENDPOINTS.CUSTOMER_DETAIL,
      product: DASHBOARD_ENDPOINTS.PRODUCT_DETAIL,
      city: DASHBOARD_ENDPOINTS.CITY_DETAIL,
    }
    const idParamMap: Record<EntityType, string> = {
      customer: 'customerId',
      product: 'productId',
      city: 'cityId',
    }
    params.set(idParamMap[entityType], entityId)
    const url = `${endpointMap[entityType]}?${params.toString()}`

    const response = await http.get<unknown>(url)
    const dto = validateEntityDetailResponse(response.data)
    return transformEntityDetailDtoToDomain(dto)
  },

  /**
   * Fetch total value of non-finished orders including 15% VAT
   */
  async getOrdersValue(): Promise<OrdersValueKpi> {
    const http = getHttpClient()
    const response = await http.get<unknown>(DASHBOARD_ENDPOINTS.ORDERS_VALUE)
    const dto = validateOrdersValueResponse(response.data)
    return {
      totalOrdersValueBeforeTax: dto.totalOrdersValueBeforeTax,
      tax: dto.tax,
      totalOrdersValueWithTax: dto.totalOrdersValueWithTax,
      currency: dto.currency,
      orderCount: dto.orderCount,
    }
  },
}
